import FormattedAlias from '@components/_shared/Translations/FormattedAlias';
import { Add, Close, Delete } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import QueryKeys from '@src/constants/queryKeys';
import useDeviceMutations from '@src/hooks/api/mutations/useDeviceMutations';
import storeConnector from '@src/store/storeConnector';
import {
  GenerateDevicePayload,
  SourceDevice,
  UnitControllersBlueprint,
} from '@src/types/Device';
import { DeviceType } from '@src/types/devices/Device.types';
import { useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';
import createDeviceDialogSx from '../../../_shared/sxStyles/Device/CreateDeviceDialogSx';

interface DialogProps {
  jsonData: GenerateDevicePayload;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setConfirmOpen: Dispatch<SetStateAction<boolean>>;
}

const DEVICE_TYPE_OPTIONS: { label: string; value: string }[] = [
  { label: 'TSC', value: DeviceType.TSC },
  { label: 'Meter Point (MP)', value: DeviceType.METER_POINT },
  { label: 'BMS', value: DeviceType.BMS },
  { label: 'Rack', value: DeviceType.RACK },
  { label: 'Meter', value: DeviceType.METER },
  { label: 'PCS', value: DeviceType.PCS },
  { label: 'Converter', value: DeviceType.CONVERTER },
  { label: 'Module', value: DeviceType.MODULE },
  { label: 'RTAC', value: DeviceType.RTAC },
  { label: 'Tracker', value: DeviceType.TRACKER },
  { label: 'Master Tracker', value: DeviceType.MASTER_TRACKER },
  { label: 'MET', value: DeviceType.MET },
  { label: 'MET Site', value: DeviceType.MET_SITE },
  { label: 'Powin EMS', value: DeviceType.POWIN_EMS },
  { label: 'Powin BESS', value: DeviceType.POWIN_BESS },
  { label: 'Powin Rack', value: DeviceType.POWIN_RACK },
];

const PARENT_TYPE_OPTIONS: { label: string; value: string }[] = [
  { label: 'Site Controller', value: DeviceType.SITE_CONTROLLER },
  { label: 'Unit Controller', value: DeviceType.UNIT_CONTROLLER },
  ...DEVICE_TYPE_OPTIONS,
];

function emptySourceDevice(): SourceDevice {
  return {
    type: '' as DeviceType,
    parentType: DeviceType.SITE_CONTROLLER,
    count: 1,
    namePrefix: '',
    nameSuffix: '',
  };
}

function GenerateDeviceDialog({
  jsonData,
  setOpen,
  setConfirmOpen,
}: DialogProps) {
  const queryClient = useQueryClient();
  const { generateDevices } = useDeviceMutations();

  const [siteControllerId, setSiteControllerId] = useState(
    jsonData?.siteControllerId ?? '',
  );

  const [includeUnitControllers, setIncludeUnitControllers] = useState(
    !!jsonData?.unitControllers,
  );
  const [unitControllers, setUnitControllers] =
    useState<UnitControllersBlueprint>({
      engineeringIds: jsonData?.unitControllers?.engineeringIds ?? [],
      namePrefix: jsonData?.unitControllers?.namePrefix ?? 'Unit ',
      nameSuffix: jsonData?.unitControllers?.nameSuffix ?? '',
    });
  const [newEngId, setNewEngId] = useState('');

  const [sourceDevices, setSourceDevices] = useState<SourceDevice[]>(
    jsonData?.sourceDevices ?? [],
  );

  const handleAddEngId = () => {
    const ids = newEngId
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id.length > 0);
    if (ids.length === 0) {
      return;
    }
    setUnitControllers((prev) => {
      const existing = new Set(prev.engineeringIds);
      const uniqueNew = ids.filter((id) => !existing.has(id));
      if (uniqueNew.length === 0) {
        return prev;
      }
      return {
        ...prev,
        engineeringIds: [...prev.engineeringIds, ...uniqueNew],
      };
    });
    setNewEngId('');
  };

  const handleRemoveEngId = (id: string) => {
    setUnitControllers((prev) => ({
      ...prev,
      engineeringIds: prev.engineeringIds.filter((e) => e !== id),
    }));
  };

  const handleAddSourceDevice = () => {
    setSourceDevices((prev) => [...prev, emptySourceDevice()]);
  };

  const handleRemoveSourceDevice = (index: number) => {
    setSourceDevices((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSourceDeviceChange = (
    index: number,
    field: keyof SourceDevice,
    value: string | number,
  ) => {
    setSourceDevices((prev) =>
      prev.map((sd, i) => (i === index ? { ...sd, [field]: value } : sd)),
    );
  };

  const buildPayload = (): GenerateDevicePayload => {
    const payload: GenerateDevicePayload = {
      siteControllerId,
    };

    if (includeUnitControllers && unitControllers.engineeringIds.length > 0) {
      payload.unitControllers = { ...unitControllers };
    }

    if (sourceDevices.length > 0) {
      payload.sourceDevices = sourceDevices.filter(
        (sd) => sd.type && sd.parentType && sd.count > 0,
      );
    }

    return payload;
  };

  const handleGenerate = () => {
    const payload = buildPayload();
    generateDevices.mutate(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.devices],
        });
        setConfirmOpen(true);
        setOpen(false);
      },
    });
  };

  const isValid =
    siteControllerId.trim().length > 0 &&
    (!includeUnitControllers || unitControllers.engineeringIds.length > 0);

  return (
    <Dialog
      open
      fullWidth
      maxWidth='md'
      PaperProps={{
        sx: {
          ...createDeviceDialogSx.dialogPaper,
          minWidth: '600px',
          maxHeight: '85vh',
        },
      }}
    >
      <DialogTitle>
        <FormattedAlias id='Generate Devices' />
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack gap={3}>
          {/* Site Controller ID */}
          <div>
            <FormLabel sx={createDeviceDialogSx.fieldLabel}>
              Site Controller Engineering ID
            </FormLabel>
            <TextField
              value={siteControllerId}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setSiteControllerId(e.target.value)
              }
              fullWidth
              size='small'
              placeholder='e.g. 000000F'
              sx={createDeviceDialogSx.textField}
            />
          </div>

          <Divider />

          {/* Unit Controllers Section */}
          <div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeUnitControllers}
                  onChange={(e) => setIncludeUnitControllers(e.target.checked)}
                />
              }
              label={
                <Typography variant='subtitle1' fontWeight={600}>
                  Include Unit Controllers
                </Typography>
              }
            />

            {includeUnitControllers && (
              <Stack gap={2} sx={{ mt: 1, pl: 1 }}>
                <Stack direction='row' gap={2}>
                  <div style={{ flex: 1 }}>
                    <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                      Name Prefix
                    </FormLabel>
                    <TextField
                      value={unitControllers.namePrefix}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setUnitControllers((prev) => ({
                          ...prev,
                          namePrefix: e.target.value,
                        }))
                      }
                      fullWidth
                      size='small'
                      placeholder='e.g. Unit '
                      sx={createDeviceDialogSx.textField}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                      Name Suffix
                    </FormLabel>
                    <TextField
                      value={unitControllers.nameSuffix}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setUnitControllers((prev) => ({
                          ...prev,
                          nameSuffix: e.target.value,
                        }))
                      }
                      fullWidth
                      size='small'
                      sx={createDeviceDialogSx.textField}
                    />
                  </div>
                </Stack>

                <div>
                  <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                    Engineering IDs
                  </FormLabel>
                  <Stack direction='row' gap={1} sx={{ mb: 1 }}>
                    <TextField
                      value={newEngId}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setNewEngId(e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddEngId();
                        }
                      }}
                      size='small'
                      placeholder='Enter comma-separated engineering IDs'
                      sx={{ ...createDeviceDialogSx.textField, flex: 1 }}
                    />
                    <Button
                      variant='outlined'
                      size='small'
                      onClick={handleAddEngId}
                      disabled={!newEngId.trim()}
                    >
                      <Add fontSize='small' />
                    </Button>
                  </Stack>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                    }}
                  >
                    {unitControllers.engineeringIds.map((id) => (
                      <Button
                        key={id}
                        size='small'
                        variant='outlined'
                        onClick={() => handleRemoveEngId(id)}
                        endIcon={<Close sx={{ fontSize: 14 }} />}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          py: 0.25,
                          px: 1,
                        }}
                      >
                        {id}
                      </Button>
                    ))}
                  </Box>
                  {unitControllers.engineeringIds.length > 0 && (
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ mt: 0.5 }}
                    >
                      {unitControllers.engineeringIds.length} unit controller(s)
                    </Typography>
                  )}
                </div>
              </Stack>
            )}
          </div>

          <Divider />

          {/* Source Devices Section */}
          <div>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              sx={{ mb: 1 }}
            >
              <Typography variant='subtitle1' fontWeight={600}>
                Source Devices
              </Typography>
              <Button
                variant='outlined'
                size='small'
                startIcon={<Add />}
                onClick={handleAddSourceDevice}
              >
                Add Device
              </Button>
            </Stack>

            <Stack gap={2}>
              {sourceDevices.map((sd, index) => (
                <Box
                  key={index}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    position: 'relative',
                  }}
                >
                  <IconButton
                    size='small'
                    onClick={() => handleRemoveSourceDevice(index)}
                    sx={{ position: 'absolute', top: 4, right: 4 }}
                  >
                    <Delete fontSize='small' />
                  </IconButton>

                  <Stack gap={2}>
                    <Stack direction='row' gap={2}>
                      <div style={{ flex: 1 }}>
                        <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                          Type
                        </FormLabel>
                        <Select
                          value={sd.type}
                          onChange={(e: SelectChangeEvent) =>
                            handleSourceDeviceChange(
                              index,
                              'type',
                              e.target.value,
                            )
                          }
                          fullWidth
                          size='small'
                          displayEmpty
                          sx={createDeviceDialogSx.textField}
                        >
                          <MenuItem value='' disabled>
                            <Typography variant='body2' color='text.secondary'>
                              Select type
                            </Typography>
                          </MenuItem>
                          {DEVICE_TYPE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </div>
                      <div style={{ flex: 1 }}>
                        <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                          Parent Type
                        </FormLabel>
                        <Select
                          value={sd.parentType}
                          onChange={(e: SelectChangeEvent) =>
                            handleSourceDeviceChange(
                              index,
                              'parentType',
                              e.target.value,
                            )
                          }
                          fullWidth
                          size='small'
                          displayEmpty
                          sx={createDeviceDialogSx.textField}
                        >
                          <MenuItem value='' disabled>
                            <Typography variant='body2' color='text.secondary'>
                              Select parent type
                            </Typography>
                          </MenuItem>
                          {PARENT_TYPE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </div>
                    </Stack>

                    <Stack direction='row' gap={2}>
                      <div style={{ flex: 1 }}>
                        <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                          Count
                        </FormLabel>
                        <TextField
                          type='number'
                          value={sd.count}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleSourceDeviceChange(
                              index,
                              'count',
                              Math.max(1, parseInt(e.target.value, 10) || 1),
                            )
                          }
                          fullWidth
                          size='small'
                          inputProps={{ min: 1 }}
                          sx={createDeviceDialogSx.textField}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                          Name Prefix
                        </FormLabel>
                        <TextField
                          value={sd.namePrefix ?? ''}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleSourceDeviceChange(
                              index,
                              'namePrefix',
                              e.target.value,
                            )
                          }
                          fullWidth
                          size='small'
                          placeholder='e.g. Megapack'
                          sx={createDeviceDialogSx.textField}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <FormLabel sx={createDeviceDialogSx.fieldLabel}>
                          Name Suffix
                        </FormLabel>
                        <TextField
                          value={sd.nameSuffix ?? ''}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleSourceDeviceChange(
                              index,
                              'nameSuffix',
                              e.target.value,
                            )
                          }
                          fullWidth
                          size='small'
                          sx={createDeviceDialogSx.textField}
                        />
                      </div>
                    </Stack>
                  </Stack>
                </Box>
              ))}

              {sourceDevices.length === 0 && (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ textAlign: 'center', py: 2 }}
                >
                  No source devices configured. Click "Add Device" to add one.
                </Typography>
              )}
            </Stack>
          </div>
        </Stack>
      </DialogContent>

      <DialogActions sx={createDeviceDialogSx.dialogActions}>
        <Button onClick={() => setOpen(false)}>
          <FormattedAlias id='Cancel' />
        </Button>
        <Button
          size='small'
          variant='contained'
          onClick={handleGenerate}
          disabled={!isValid}
        >
          <FormattedAlias id='Generate' />
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default storeConnector(GenerateDeviceDialog, {
  user: ['role'],
});
